import { useCallback, useContext, useEffect, useRef } from "react";
import { Device } from "@capacitor/device";
import { Capacitor } from "@capacitor/core";
import { PortalContext } from "../context/Provider";
import { GlobalContext } from "global/context/Provider";
import {
  wsGetDispositivoPacienteAsync,
  wsPostDispositivoPacienteAsync,
  wsPostDispositivoPacienteTransferirAsync,
  wsDeleteDispositivoPacienteAsync,
  wsPatchDispositivoPreferenciasAsync,
} from "../context/Actions/dispositivoPaciente/dispositivoPaciente";
import {
  DISPOSITIVOPACIENTE_SUCCESS,
  DISPOSITIVOPACIENTE_RESET,
} from "../context/ActionTypes";
import { showModal, hideModal } from "../context/Actions/modal/modal";
import { showToaster } from "global/context/action/toaster/toaster";

// Constante — se evalúa una vez al cargar el módulo (no en cada render).
const IS_NATIVE_PLATFORM = Capacitor.isNativePlatform();

// Flags a nivel de módulo — sobreviven remounts del componente. Necesarios
// porque PushBootstrap (que monta este hook) puede remontarse por cambios
// en el árbol de arriba (config, layout, navegación). Sin esto, cada remount
// re-dispararía el flow desde cero y podríamos hacer múltiples POSTs.
// Se resetean en el efecto cuando el user hace logout.
//
// DISEÑO SINGLETON: este hook está pensado para ser montado UNA SOLA VEZ en la
// app (dentro de PushBootstrap). Los flags de módulo asumen esa condición. Si
// en el futuro se monta en múltiples árboles simultáneos, hay que refactorizar
// a un context/ref compartido explícito para evitar races entre instancias.
let flowInFlight = false;
let flowCompleted = false;
// fcmToken que HOY esta guardado en el back para este paciente+device (o el que
// no conviene pisar, ver caso "otro device"). El patch tardio compara contra este
// valor: si el token del celular es distinto, lo reenvia. Importante: nunca se
// marca un token como registrado si no llego al back — antes se marcaba el token
// del celular aunque el back tuviera null, y el null quedaba para siempre.
let lastRegisteredFcmToken = null;
// El paciente tiene OTRO device registrado y le mostramos el modal de revincular.
// Mientras no decida, no se reenvia token: el POST borraria su otro device.
let esperandoRevinculacion = false;

/**
 * Hook que gestiona la vinculación del dispositivo del paciente con el backend.
 * Se ejecuta post-login (solo mobile) y realiza un flow lineal:
 *
 *   1. Obtiene el deviceId del hardware.
 *   2. GET al back para ver si el paciente tiene un device registrado.
 *   3a. Si ya tiene ESTE device → si el fcmToken del back difiere del actual
 *       (null por un login anterior fallido, o token rotado/vencido) lo reenvia.
 *   3b. Si tiene OTRO device (misma cuenta cambió de celu) → modal revincular.
 *   3c. Si no tiene ninguno → POST para registrar.
 *       - Si el back responde 409 device_owned_by_other (el device es de otro
 *         paciente) → transferencia AUTOMÁTICA sin preguntar al user.
 *
 * Diseño: el flow es async con await en vez de useEffects encadenados +
 * flags booleanos. Los flags de "ya ejecuté" viven a nivel de módulo para
 * sobrevivir remounts. Al hacer logout, se resetean.
 *
 * Handlers expuestos (para consumers como Perfil que necesitan revincular /
 * desvincular manualmente):
 *   - resetearDispositivo(): limpia state local (sin borrar en el back)
 *   - revincularDispositivo(): DELETE + POST para el device actual
 */
const useDispositivoVinculacion = () => {
  const {
    dispositivoPacienteState,
    dispositivoPacienteDispatch,
    modalDispatch,
    tokenFCMState,
  } = useContext(PortalContext);
  const { authState, toasterDispatch } = useContext(GlobalContext);

  const fcmToken = tokenFCMState?.tokenFCM?.newToken ?? null;

  // Preferencia de push vigente en el back (la trae el GET). Se lee via ref para
  // no re-disparar efectos cuando cambia desde el toggle de Perfil.
  const notificacionesActivasRef = useRef(true);
  const notifActivasState =
    dispositivoPacienteState?.dispositivoPaciente?.data?.notificacionesActivas;
  useEffect(() => {
    if (typeof notifActivasState === "boolean") {
      notificacionesActivasRef.current = notifActivasState;
    }
  }, [notifActivasState]);

  // ==================== Helpers ====================

  const getDeviceInfo = useCallback(async () => {
    try {
      const idInfo = await Device.getId();
      const id = idInfo.identifier;
      if (!id) return null;
      const info = await Device.getInfo();
      return {
        id,
        descripcion: info.model || "Dispositivo Móvil",
      };
    } catch (err) {
      console.error("[Vinc] Device.getId/getInfo failed:", err);
      return null;
    }
  }, []);

  const showModalRevincular = useCallback(
    (onConfirmar) => {
      showModal(
        "Genericos/FCMTokenModal/FCMTokenModal",
        {
          dismiss: () => hideModal()(modalDispatch),
          desvincular: () => {
            hideModal()(modalDispatch);
            onConfirmar();
          },
        },
        [],
        "centro",
        true
      )(modalDispatch);
    },
    [modalDispatch]
  );

  const toasterSuccess = useCallback(
    (texto) => {
      showToaster({ texto, tipo: "success" }, "centroArriba")(toasterDispatch);
    },
    [toasterDispatch]
  );

  const toasterError = useCallback(
    (texto) => {
      showToaster({ texto, tipo: "danger" }, "centroArriba")(toasterDispatch);
    },
    [toasterDispatch]
  );

  const sincronizarState = useCallback(
    (device, token, notificacionesActivas) => {
      dispositivoPacienteDispatch({
        type: DISPOSITIVOPACIENTE_SUCCESS,
        payload: {
          idApp: device.id,
          descripcion: device.descripcion,
          fcmToken: token,
          notificacionesActivas,
        },
      });
    },
    [dispositivoPacienteDispatch]
  );

  /**
   * Reenvia el fcmToken de un device que YA es del paciente (upsert POST).
   * El back hace DELETE + INSERT y deja NOTIFICACIONES_ACTIVAS=1, asi que si el
   * paciente las tenia apagadas las volvemos a apagar con el PATCH.
   * Devuelve true si el token quedo guardado.
   */
  const actualizarToken = useCallback(
    async (device, token, notificacionesActivas) => {
      const result = await wsPostDispositivoPacienteAsync({
        id: device.id,
        descripcion: device.descripcion,
        fcmToken: token,
      });
      if (result.error) {
        console.error("[Vinc] actualizacion del FCM token fallo:", result.error);
        return false;
      }
      lastRegisteredFcmToken = token;

      if (notificacionesActivas === false) {
        const p = await wsPatchDispositivoPreferenciasAsync({
          notificacionesActivas: false,
        });
        if (p.error) {
          console.error("[Vinc] no se pudo restaurar la preferencia de push:", p.error);
        }
      }
      sincronizarState(device, token, notificacionesActivas !== false);
      return true;
    },
    [sincronizarState]
  );

  // ==================== Flow principal ====================

  const registrarDevice = useCallback(
    async (device) => {
      const result = await wsPostDispositivoPacienteAsync({
        id: device.id,
        descripcion: device.descripcion,
        fcmToken,
      });

      // 409 device_owned_by_other → transferencia AUTOMÁTICA.
      // Decisión de negocio (dirección): el último paciente que loguea toma
      // ownership del device sin confirmación previa. Antes había un modal
      // preguntando al user si quería transferir — se removió.
      if (
        result.status === 409 &&
        result.error?.error === "device_owned_by_other"
      ) {
        const t = await wsPostDispositivoPacienteTransferirAsync({
          id: device.id,
          descripcion: device.descripcion,
          fcmToken,
        });
        if (t.error) {
          toasterError(
            typeof t.error === "string"
              ? t.error
              : "No se pudo transferir el dispositivo"
          );
          return;
        }
        lastRegisteredFcmToken = fcmToken;
        sincronizarState(device, fcmToken, true);
        toasterSuccess("Dispositivo vinculado correctamente");
        return;
      }

      if (result.error) {
        toasterError(
          typeof result.error === "string"
            ? result.error
            : "Error al vincular dispositivo"
        );
        return;
      }

      // Éxito → sincronizamos el state para que la UI (Perfil) refleje el registro.
      lastRegisteredFcmToken = fcmToken;
      sincronizarState(device, fcmToken, true);
      toasterSuccess("Dispositivo vinculado correctamente");
    },
    [fcmToken, sincronizarState, toasterSuccess, toasterError]
  );

  const runFlow = useCallback(async () => {
    const device = await getDeviceInfo();
    if (!device) return;

    const getResult = await wsGetDispositivoPacienteAsync();

    // 404 o data null/vacío → no hay device registrado → registrar directo.
    const registrado = getResult.data;
    // El back devuelve el campo como `idApp` (camelCase — DTO C# IdApp).
    const registradoIdApp = registrado?.idApp || registrado?.idapp;

    const noHayRegistro =
      getResult.status === 404 ||
      registrado === null ||
      registrado === "" ||
      registrado === undefined ||
      (registrado && typeof registrado === "object" && !registradoIdApp);

    if (noHayRegistro) {
      await registrarDevice(device);
      return;
    }

    // Sincronizamos el state con lo que devolvió el back (para la UI de Perfil).
    dispositivoPacienteDispatch({
      type: DISPOSITIVOPACIENTE_SUCCESS,
      payload: registrado,
    });

    // Caso 1: ya tiene ESTE device registrado. Verificamos que el token guardado
    // sea el del celular: puede haber quedado null (login previo sin token) o
    // viejo (Firebase lo roto/invalido). Si difiere, lo reenviamos.
    if (registradoIdApp === device.id) {
      const tokenEnBack = registrado.fcmToken || null;
      lastRegisteredFcmToken = tokenEnBack;
      if (fcmToken && fcmToken !== tokenEnBack) {
        await actualizarToken(device, fcmToken, registrado.notificacionesActivas);
      }
      return;
    }

    // Caso 2: tiene OTRO device (misma cuenta cambió de celu) → modal revincular.
    // Mientras el user no decida, el patch tardio no puede registrar este device
    // por su cuenta (el POST borraria el otro device sin que el user lo elija).
    esperandoRevinculacion = true;
    showModalRevincular(async () => {
      const del = await wsDeleteDispositivoPacienteAsync();
      if (del.error) {
        toasterError("No se pudo desvincular el dispositivo anterior");
        return;
      }
      esperandoRevinculacion = false;
      // Usa el token mas reciente: el modal puede confirmarse mucho despues del flow.
      await registrarDeviceRef.current(device);
    });
  }, [
    getDeviceInfo,
    dispositivoPacienteDispatch,
    registrarDevice,
    actualizarToken,
    showModalRevincular,
    toasterError,
    fcmToken,
  ]);

  // Refs a los valores mas recientes, para callbacks que corren despues de un
  // await (fin del flow, confirmacion del modal) y no deben usar una closure vieja.
  const fcmTokenActualRef = useRef(fcmToken);
  fcmTokenActualRef.current = fcmToken;
  const registrarDeviceRef = useRef(registrarDevice);
  registrarDeviceRef.current = registrarDevice;
  const actualizarTokenRef = useRef(actualizarToken);
  actualizarTokenRef.current = actualizarToken;

  /**
   * Si el token del celular no es el que quedo en el back, lo reenvia.
   * Lo usan el patch tardio y el fin del flow inicial (el token pudo llegar
   * mientras el flow corria, cuando el patch tardio todavia no podia actuar).
   */
  const sincronizarTokenSiHaceFalta = useCallback(async () => {
    const token = fcmTokenActualRef.current;
    if (!token || esperandoRevinculacion) return;
    if (lastRegisteredFcmToken === token) return;
    const device = await getDeviceInfo();
    if (!device) return;
    // Re-chequeo post-await: otro camino pudo registrarlo mientras tanto.
    if (lastRegisteredFcmToken === fcmTokenActualRef.current) return;
    await actualizarTokenRef.current(
      device,
      fcmTokenActualRef.current,
      notificacionesActivasRef.current
    );
  }, [getDeviceInfo]);

  // ==================== Efecto: dispara el flow post-login ====================

  useEffect(() => {
    if (!IS_NATIVE_PLATFORM) return;

    // Logout → reset de flags para permitir un nuevo flow en el próximo login.
    if (!authState?.auth?.data) {
      flowInFlight = false;
      flowCompleted = false;
      lastRegisteredFcmToken = null;
      esperandoRevinculacion = false;
      return;
    }

    // Guard contra doble ejecución (remount del componente / re-render).
    if (flowInFlight || flowCompleted) return;

    const dispatchFlow = () => {
      if (flowInFlight || flowCompleted) return;
      flowInFlight = true;
      runFlow()
        .then(() => {
          flowCompleted = true;
          return sincronizarTokenSiHaceFalta();
        })
        .catch((err) => {
          console.error("[Vinc] runFlow error:", err);
        })
        .finally(() => {
          flowInFlight = false;
        });
    };

    // Race condition: FCM `registration` puede disparar unos segundos DESPUÉS
    // del login. Si corremos el flow antes de que llegue el token, mandamos
    // null al back y el registro queda con fcm_token=null (no puede recibir push).
    //
    // Si ya tenemos token → ejecutar inmediato.
    // Si no → esperar hasta 5s por si el listener FCM dispara. Si no llega
    // (user rechazó permisos, error del plugin, etc.), corremos igual con null
    // para al menos registrar el device; el patch tardío lo completa después.
    if (fcmToken) {
      dispatchFlow();
      return;
    }

    const timeout = setTimeout(dispatchFlow, 5000);
    return () => clearTimeout(timeout);
  }, [authState?.auth?.data, fcmToken, runFlow, sincronizarTokenSiHaceFalta]);

  // ==================== Efecto: PATCH tardío del FCM token ====================
  //
  // Escenario: el listener FCM "registration" dispara DESPUÉS de que el flow
  // inicial terminó con token=null (primera instalación, permisos aceptados más
  // tarde), o Firebase rota el token con la app abierta. Sin este efecto, el
  // device queda en el back con fcm_token=null o desactualizado → nunca recibe push.
  //
  // Si el token llega MIENTRAS el flow corre, este efecto no actua (flowCompleted
  // todavia es false); lo cubre el sincronizarTokenSiHaceFalta del fin del flow.
  useEffect(() => {
    if (!IS_NATIVE_PLATFORM) return;
    if (!authState?.auth?.data) return;
    if (!flowCompleted) return;
    if (!fcmToken) return;
    sincronizarTokenSiHaceFalta();
  }, [authState?.auth?.data, fcmToken, sincronizarTokenSiHaceFalta]);

  // ==================== Handlers públicos ====================

  const resetearDispositivo = useCallback(() => {
    flowInFlight = false;
    flowCompleted = false;
    lastRegisteredFcmToken = null;
    esperandoRevinculacion = false;
    dispositivoPacienteDispatch({ type: DISPOSITIVOPACIENTE_RESET });
  }, [dispositivoPacienteDispatch]);

  const revincularDispositivo = useCallback(async () => {
    const device = await getDeviceInfo();
    if (!device) return;
    const del = await wsDeleteDispositivoPacienteAsync();
    if (del.error) {
      toasterError("No se pudo desvincular el dispositivo anterior");
      return;
    }
    esperandoRevinculacion = false;
    await registrarDevice(device);
  }, [getDeviceInfo, registrarDevice, toasterError]);

  return {
    dispositivoActual: dispositivoPacienteState.dispositivoPaciente.data,
    resetearDispositivo,
    revincularDispositivo,
    isLoading: dispositivoPacienteState.dispositivoPaciente.loading,
  };
};

export default useDispositivoVinculacion;
